import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RefreshIcon from '@mui/icons-material/Refresh';
import { DataGrid, GridColDef, GridPaginationModel, GridRenderCellParams } from '@mui/x-data-grid';

import { fetchCostCenters } from '../forms/forms.apis';
import {
  fetchActivitiesList,
  fetchActivityTypes,
} from './activity.apis';
import { ILookupItem } from '../forms/forms.models';
import {
  IActivityListFilters,
  IActivityListItem,
} from './activity.models';

type Props = {
  navigate?: (path: string) => void;
};

type State = {
  loading: boolean;
  error: string;
  rows: IActivityListItem[];
  total: number;

  filters: IActivityListFilters;
  activityTypes: ILookupItem[];
  costcenters: ILookupItem[];
};

const DEFAULT_FILTERS: IActivityListFilters = {
  search: '',
  typeix: '',
  costcenterix: '',
  status: '',
  page: 1,
  pagesize: 20,
};

export default class ActivityListPage extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      loading: false,
      error: '',
      rows: [],
      total: 0,

      filters: { ...DEFAULT_FILTERS },
      activityTypes: [],
      costcenters: [],
    };
  }

  componentDidMount(): void {
    this.loadInitialData();
  }

  private loadInitialData = async (): Promise<void> => {
    this.setState({
      loading: true,
      error: '',
    });

    try {
      const [activityTypes, costcenters] = await Promise.all([
        fetchActivityTypes(),
        fetchCostCenters(),
      ]);

      this.setState(
        {
          activityTypes,
          costcenters,
        },
        () => this.loadRows()
      );
    } catch (error: any) {
      this.setState({
        loading: false,
        error: error?.message || 'Failed to load activity lookups',
      });
    }
  };

  private loadRows = async (): Promise<void> => {
    this.setState({
      loading: true,
      error: '',
    });

    try {
      const result = await fetchActivitiesList(this.state.filters);

      this.setState({
        rows: result.items || [],
        total: result.total || 0,
        loading: false,
      });
    } catch (error: any) {
      this.setState({
        loading: false,
        error: error?.message || 'Failed to load activities',
      });
    }
  };

  private handleTextChange =
    (field: keyof IActivityListFilters) =>
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const value = event.target.value;
      this.setState((prevState) => ({
        filters: {
          ...prevState.filters,
          [field]: value,
          page: 1,
        },
      }));
    };

  private handleSelectChange =
    (field: keyof IActivityListFilters) =>
    (event: SelectChangeEvent<string>): void => {
      const value = event.target.value;
      this.setState((prevState) => ({
        filters: {
          ...prevState.filters,
          [field]: value,
          page: 1,
        },
      }));
    };

  private handleSearch = (): void => {
    this.loadRows();
  };

  private handleReset = (): void => {
    this.setState(
      {
        filters: { ...DEFAULT_FILTERS },
      },
      () => this.loadRows()
    );
  };

  private handlePaginationChange = (model: GridPaginationModel): void => {
    this.setState(
      (prevState) => ({
        filters: {
          ...prevState.filters,
          page: model.page + 1,
          pagesize: model.pageSize,
        },
      }),
      () => this.loadRows()
    );
  };

  private handleNew = (): void => {
    const path = '/forms/activities/new';
    if (this.props.navigate) {
      this.props.navigate(path);
      return;
    }
    window.location.href = path;
  };

  private handleEdit = (row: IActivityListItem): void => {
    const path = `/forms/activities/${row.recix}/edit`;
    if (this.props.navigate) {
      this.props.navigate(path);
      return;
    }
    window.location.href = path;
  };

  private handleOpen = (row: IActivityListItem): void => {
    const path = `/forms/activities/${row.recix}/edit`;
    if (this.props.navigate) {
      this.props.navigate(path);
      return;
    }
    window.location.href = path;
  };

  private getColumns = (): GridColDef<IActivityListItem>[] => {
    return [
      {
        field: 'shortcode',
        headerName: 'Code',
        minWidth: 140,
        flex: 0.8,
      },
      {
        field: 'recname',
        headerName: 'Activity',
        minWidth: 220,
        flex: 1.4,
      },
      {
        field: 'type',
        headerName: 'Type',
        minWidth: 160,
        flex: 0.9,
        valueGetter: (_value, row) => row.type?.recname || '',
      },
      {
        field: 'costcenter',
        headerName: 'Cost Center',
        minWidth: 160,
        flex: 0.9,
        valueGetter: (_value, row) => row.costcenter?.recname || '',
      },
      {
        field: 'parent',
        headerName: 'Parent Activity',
        minWidth: 180,
        flex: 1,
        valueGetter: (_value, row) => row.parent?.recname || '',
      },
      {
        field: 'status',
        headerName: 'Status',
        minWidth: 110,
        flex: 0.6,
        renderCell: (params: GridRenderCellParams<IActivityListItem, number>) =>
          params.row.status === 1 ? (
            <Chip size="small" color="success" label="Active" />
          ) : (
            <Chip size="small" label="Disabled" />
          ),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        minWidth: 170,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams<IActivityListItem>) => (
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              startIcon={<VisibilityIcon />}
              onClick={() => this.handleOpen(params.row)}
            >
              Open
            </Button>
            <Button
              size="small"
              startIcon={<EditIcon />}
              onClick={() => this.handleEdit(params.row)}
            >
              Edit
            </Button>
          </Stack>
        ),
      },
    ];
  };

  render(): React.ReactNode {
    const {
      loading,
      error,
      rows,
      total,
      filters,
      activityTypes,
      costcenters,
    } = this.state;

    return (
      <Box sx={{ p: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h5" fontWeight={700}>
              Activities
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage activity masters for activity-based budgeting.
            </Typography>
          </Box>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={this.handleNew}
          >
            New Activity
          </Button>
        </Stack>

        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="Search"
                  value={filters.search}
                  onChange={this.handleTextChange('search')}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 3 }}>
                <FormControl fullWidth>
                  <InputLabel id="activity-type-filter-label">Type</InputLabel>
                  <Select
                    labelId="activity-type-filter-label"
                    value={filters.typeix}
                    label="Type"
                    onChange={this.handleSelectChange('typeix')}
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    {activityTypes.map((item) => (
                      <MenuItem key={item.recix} value={item.recix}>
                        {item.recname}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 3 }}>
                <FormControl fullWidth>
                  <InputLabel id="activity-cc-filter-label">Cost Center</InputLabel>
                  <Select
                    labelId="activity-cc-filter-label"
                    value={filters.costcenterix}
                    label="Cost Center"
                    onChange={this.handleSelectChange('costcenterix')}
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    {costcenters.map((item) => (
                      <MenuItem key={item.recix} value={item.recix}>
                        {item.recname}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 2 }}>
                <FormControl fullWidth>
                  <InputLabel id="activity-status-filter-label">Status</InputLabel>
                  <Select
                    labelId="activity-status-filter-label"
                    value={filters.status}
                    label="Status"
                    onChange={this.handleSelectChange('status')}
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    <MenuItem value="1">Active</MenuItem>
                    <MenuItem value="0">Disabled</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Stack direction="row" spacing={1}>
                  <Button variant="contained" onClick={this.handleSearch}>
                    Search
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={this.handleReset}
                  >
                    Reset
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        <Card>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ height: 640, width: '100%' }}>
              {loading && rows.length === 0 ? (
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  sx={{ height: '100%' }}
                  spacing={2}
                >
                  <CircularProgress />
                  <Typography variant="body2" color="text.secondary">
                    Loading activities...
                  </Typography>
                </Stack>
              ) : (
                <DataGrid
                  rows={rows}
                  columns={this.getColumns()}
                  getRowId={(row) => row.recix}
                  rowCount={total}
                  loading={loading}
                  paginationMode="server"
                  pageSizeOptions={[10, 20, 50, 100]}
                  paginationModel={{
                    page: filters.page - 1,
                    pageSize: filters.pagesize,
                  }}
                  onPaginationModelChange={this.handlePaginationChange}
                  disableRowSelectionOnClick
                  sx={{ border: 0 }}
                />
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }
}